import {Injectable} from '@angular/core';
import {Params, Router} from '@angular/router';
import {Observable, of, race, timer} from 'rxjs';
import {filter, map, switchMap, take} from 'rxjs/operators';
import {AuditId, DeviceId, ProjectId, TestObjectId, TestRunId} from '@webmate-web-core/shared/types/ids';
import {BrowserSessionInfo} from '@webmate-web-core/shared/types/browsersession';
import {toTitleCase} from '@webmate-web-core/shared/utils/utils';
import {WorkbenchIpcRegistryService} from './workbench-ipc-registry.service';
import {WorkbenchIpcService} from './workbench-ipc.service';
import {WorkbenchIpcMessage, WorkbenchMode, WorkbenchRegistryEntry} from './workbench-ipc.types';
import {WorkbenchFocusOutcome, focusWorkbenchWindowByName, workbenchWindowName} from './workbench-window-focus';
import {WmNotificationService} from '../components/notification/wm-notification.service';
import {UserCustomizationService} from '../services/user-customization.service';

export interface WorkbenchOpenRequest {
    projectId: ProjectId;
    auditId?: AuditId;
    deviceId?: string;
    activeTestRun?: TestRunId;
    autoStartManualTest?: boolean;
    suppressAutoResumeTestRun?: boolean;
    browserExpeditionStartConfig?: unknown;
    openTestObjectInExpeditionConfig?: {testObjectId: TestObjectId};
    childWorkbench?: boolean;
    popoutConsoleId?: string;
}

export interface WorkbenchOpenOptions {
    blockedPopupMessage?: string;
}

export type WorkbenchOpenResult = {
    type: 'focused';
    host: WorkbenchRegistryEntry;
} | {
    type: 'opened';
    openedWindow: Window | null;
} | {
    type: 'cancelled';
};

/**
 * Result of switching the active project: either a regular open/focus result, or a full in-tab
 * navigation when no existing host could be focused.
 */
export type WorkbenchProjectSwitchResult = WorkbenchOpenResult | {
    type: 'navigated';
    url: string;
};

const MODE_LABEL: Record<WorkbenchMode, string> = {
    'default': 'default',
    'manual-test': 'manual test',
    'audit': 'audit'
};

@Injectable({
    providedIn: 'root'
})
export class WorkbenchOpenService {
    private static readonly BLOCKED_CONSOLE_POPUP_MESSAGE =
        'Your browser has blocked this console, please allow pop-ups / new tabs for this webpage!';

    private readonly openedWindows = new Map<ProjectId, Window>();

    constructor(private readonly router: Router,
                private readonly workbenchIpcService: WorkbenchIpcService,
                private readonly registryService: WorkbenchIpcRegistryService,
                private readonly notificationService: WmNotificationService,
                private readonly userCustomizationService: UserCustomizationService) {
    }

    openWorkbench(request: WorkbenchOpenRequest, options: WorkbenchOpenOptions = {}): Observable<WorkbenchOpenResult> {
        // The workbench we previously opened for this project is gone, but its registry entry may
        // still be inside the freshness window. Drop the dead handle and demand a live claim rather
        // than trusting the cache — opening a new tab outright would spawn a second workbench that
        // fights an existing host for ownership.
        const cachedHostDied = this.hasClosedCachedWindow(request.projectId);
        if (cachedHostDied) {
            this.openedWindows.delete(request.projectId);
        } else {
            const freshHost = this.registryService.getFreshHost(request.projectId);
            if (freshHost) {
                return this.gateAndForward(freshHost, request);
            }
        }

        return this.registryService.queryHost(request.projectId, undefined, {ignoreCachedEntries: cachedHostDied}).pipe(
            take(1),
            switchMap(host => host
                ? this.gateAndForward(host, request)
                : of(this.openNewWorkbenchWindow(request, options)))
        );
    }

    /**
     * Opens the workbench console for an already-running browser session on the given device.
     */
    openConsole(projectId: ProjectId, deviceId: DeviceId): Observable<WorkbenchOpenResult> {
        return this.openWorkbench({projectId, deviceId}, {
            blockedPopupMessage: WorkbenchOpenService.BLOCKED_CONSOLE_POPUP_MESSAGE
        });
    }

    /**
     * Opens the workbench console for a browser session that has become ready/running. Warns and
     * does nothing if the session has no device assigned yet.
     */
    openConsoleForBrowserSession(projectId: ProjectId, session: BrowserSessionInfo): void {
        if (!session.optDeviceId) {
            console.warn('expedition has no device id yet is running. Something is wrong.');
            return;
        }
        this.openConsole(projectId, session.optDeviceId).pipe(take(1)).subscribe();
    }

    focusWorkbench(request: WorkbenchOpenRequest): Observable<WorkbenchOpenResult | undefined> {
        const freshHost = this.registryService.getFreshHost(request.projectId);
        if (freshHost) {
            return this.gateAndForward(freshHost, request);
        }
        return this.registryService.queryHost(request.projectId).pipe(
            take(1),
            switchMap(host => host ? this.gateAndForward(host, request) : of(undefined))
        );
    }

    focusExistingWorkbench(projectId: ProjectId): Observable<WorkbenchOpenResult | undefined> {
        if (this.hasClosedCachedWindow(projectId)) {
            this.openedWindows.delete(projectId);
        }

        const freshHost = this.registryService.getFreshHost(projectId);
        if (freshHost) {
            this.focusHostWindow(freshHost, projectId);
            return of({type: 'focused', host: freshHost});
        }

        return this.registryService.queryHost(projectId).pipe(
            take(1),
            map(host => {
                if (!host) {
                    return undefined;
                }
                this.focusHostWindow(host, projectId);
                return {type: 'focused', host} as WorkbenchOpenResult;
            })
        );
    }

    /**
     * Switches the active project by opening it in a new workbench tab.
     *
     * The target project's mode is derived from its own workflow anchors at bootstrap; we do not
     * forward the source project's mode (or any mode hint) — that would reintroduce stale-mode bugs.
     */
    switchProjectInNewTab(projectId: ProjectId): Observable<WorkbenchOpenResult> {
        return this.focusExistingWorkbench(projectId).pipe(
            switchMap(result => result ? of(result) : this.openWorkbench({projectId}))
        );
    }

    /**
     * Switches the active project by reusing the current tab: focuses an existing host for the
     * target project if one is live, otherwise navigates the current tab to the project's workbench.
     */
    switchProjectInCurrentTab(projectId: ProjectId): Observable<WorkbenchProjectSwitchResult> {
        return this.focusExistingWorkbench(projectId).pipe(
            map(result => {
                if (result) {
                    return result;
                }
                const url = this.createWorkbenchUrl({projectId});
                // Navigate the current tab to the target project's workbench (full reload).
                window.open(url, '_self');
                return {type: 'navigated', url} as const;
            })
        );
    }

    /**
     * Sends a workflow-activation IPC to an existing host only. When the request would overwrite a different
     * non-default mode the user is asked to confirm before the IPC is sent. Use `openWorkbench(...)` for entry
     * points that should create a new workbench when the project is not already open.
     */
    setMode(projectId: ProjectId, activeMode: WorkbenchMode): Observable<WorkbenchOpenResult | undefined> {
        return this.confirmExternalModeSwitchIfNeeded(projectId, activeMode).pipe(
            switchMap(proceed => {
                if (!proceed) {
                    return of<WorkbenchOpenResult | undefined>({type: 'cancelled'});
                }
                const freshHost = this.registryService.getFreshHost(projectId);
                if (freshHost) {
                    this.workbenchIpcService.setMode(projectId, {activeMode});
                    this.focusHostWindow(freshHost, projectId);
                    return of<WorkbenchOpenResult | undefined>({type: 'focused', host: freshHost});
                }
                return this.registryService.queryHost(projectId).pipe(
                    take(1),
                    map<WorkbenchRegistryEntry | undefined, WorkbenchOpenResult | undefined>(host => {
                        if (!host) {
                            return undefined;
                        }
                        this.workbenchIpcService.setMode(projectId, {activeMode});
                        this.focusHostWindow(host, projectId);
                        return {type: 'focused', host};
                    })
                );
            })
        );
    }

    requiresExternalModeSwitchConfirmation(projectId: ProjectId, requestedMode: WorkbenchMode): Observable<boolean> {
        const freshHost = this.registryService.getFreshHost(projectId);
        if (freshHost && this.isWorkbenchMode(freshHost.activeMode)) {
            return of(this.isReplacingDifferentNonDefaultMode(freshHost.activeMode, requestedMode));
        }
        return this.registryService.queryHost(projectId).pipe(
            take(1),
            switchMap(host => {
                if (!host) {
                    return of(false);
                }
                if (this.isWorkbenchMode(host.activeMode)) {
                    return of(this.isReplacingDifferentNonDefaultMode(host.activeMode, requestedMode));
                }
                return this.queryModeState(projectId).pipe(
                    map(activeMode => activeMode ? this.isReplacingDifferentNonDefaultMode(activeMode, requestedMode) : false)
                );
            })
        );
    }

    /**
     * Resolves with `true` when the requested mode switch may proceed, `false` when the user cancelled
     * a confirmation dialog. When no confirmation is required the gate resolves immediately.
     */
    confirmExternalModeSwitchIfNeeded(projectId: ProjectId, requestedMode: WorkbenchMode): Observable<boolean> {
        return this.requiresExternalModeSwitchConfirmation(projectId, requestedMode).pipe(
            switchMap(needs => needs ? this.askUserToConfirmModeSwitch(projectId, requestedMode) : of(true))
        );
    }

    /**
     * Mode implied by an open/focus request. Anchors only — there is no `activeMode` field on the request.
     */
    requestedModeFromOpenRequest(request: WorkbenchOpenRequest): WorkbenchMode {
        if (request.auditId) {
            return 'audit';
        }
        if (request.activeTestRun || request.autoStartManualTest !== undefined || request.suppressAutoResumeTestRun) {
            return 'manual-test';
        }
        return 'default';
    }

    private askUserToConfirmModeSwitch(projectId: ProjectId, requestedMode: WorkbenchMode): Observable<boolean> {
        return new Observable<boolean>(subscriber => {
            const currentMode = this.registryService.getFreshHost(projectId)?.activeMode;
            const currentLabel = currentMode && this.isWorkbenchMode(currentMode) ? MODE_LABEL[currentMode] : 'another';
            const msg = `The workbench for this project is currently in ${currentLabel} mode. ` +
                `Switch it to ${MODE_LABEL[requestedMode]} mode?`;
            const opened = this.notificationService.openWithConfig({
                msg,
                confirmText: `Switch to ${toTitleCase(MODE_LABEL[requestedMode])}`,
                confirmCallback: () => {
                    subscriber.next(true);
                    subscriber.complete();
                },
                cancelText: 'Cancel',
                cancelCallback: () => {
                    subscriber.next(false);
                    subscriber.complete();
                }
            });
            if (!opened) {
                subscriber.next(false);
                subscriber.complete();
            }
        });
    }

    private gateAndForward(host: WorkbenchRegistryEntry, request: WorkbenchOpenRequest): Observable<WorkbenchOpenResult> {
        const requestedMode = this.requestedModeFromOpenRequest(request);
        if (requestedMode === 'default') {
            return of(this.forwardToHost(host, request));
        }
        return this.confirmExternalModeSwitchIfNeeded(request.projectId, requestedMode).pipe(
            map(proceed => proceed
                ? this.forwardToHost(host, request)
                : ({type: 'cancelled'} as WorkbenchOpenResult))
        );
    }

    private forwardToHost(host: WorkbenchRegistryEntry, request: WorkbenchOpenRequest): WorkbenchOpenResult {
        this.recordAuditOpen(request);
        const startupAction = {
            kind: 'workbench-init',
            params: {...request}
        };
        this.workbenchIpcService.open(request.projectId, {startupAction});
        this.focusHostWindow(host, request.projectId);
        return {
            type: 'focused',
            host
        };
    }

    private openNewWorkbenchWindow(request: WorkbenchOpenRequest, options: WorkbenchOpenOptions): WorkbenchOpenResult {
        const openedWindow = window.open(this.createWorkbenchUrl(request), workbenchWindowName(request.projectId));
        if (openedWindow) {
            this.recordAuditOpen(request);
            openedWindow.focus();
            this.openedWindows.set(request.projectId, openedWindow);
        } else if (options.blockedPopupMessage) {
            alert(options.blockedPopupMessage);
        }
        return {
            type: 'opened',
            openedWindow
        };
    }

    private queryModeState(projectId: ProjectId): Observable<WorkbenchMode | undefined> {
        const correlationId = `workbench-mode-state-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        this.workbenchIpcService.modeState(projectId, {requestedAt: Date.now()}, correlationId, 'webmate');
        const response$ = this.workbenchIpcService.observeCorrelation(correlationId).pipe(
            filter((message): message is WorkbenchIpcMessage<'workbench:mode-state'> => message.type === 'workbench:mode-state'),
            map(message => message.payload.activeMode),
            filter((activeMode): activeMode is WorkbenchMode => this.isWorkbenchMode(activeMode)),
            take(1)
        );
        return race(response$, timer(300).pipe(map(() => undefined)));
    }

    private isReplacingDifferentNonDefaultMode(currentMode: WorkbenchMode, requestedMode: WorkbenchMode): boolean {
        return currentMode !== 'default' && requestedMode !== 'default' && currentMode !== requestedMode;
    }

    private isWorkbenchMode(value: unknown): value is WorkbenchMode {
        return value === 'default' || value === 'manual-test' || value === 'audit';
    }

    /**
     * Brings the existing workbench tab to the foreground. Tries the cached Window reference first; after a
     * webmate refresh — or in any webmate tab that did not itself open the workbench — that reference is
     * gone, so we fall back to resolving the workbench by browsing-context name.
     *
     * That name resolves only inside this tab's own browsing-context group, so it can genuinely fail. When
     * it does we tell the user rather than opening a duplicate workbench: the request itself has already
     * been delivered to the host over IPC, only the visual focus is lost.
     */
    private focusHostWindow(host: WorkbenchRegistryEntry, projectId: ProjectId): WorkbenchFocusOutcome {
        const cached = this.openedWindows.get(projectId);
        if (cached && !cached.closed) {
            cached.focus();
            return 'focused';
        }
        if (cached) {
            this.openedWindows.delete(projectId);
        }

        const result = focusWorkbenchWindowByName(window, projectId, host.windowName);
        if (result.outcome === 'focused' && result.window) {
            this.openedWindows.set(projectId, result.window);
        } else {
            this.notifyWorkbenchOutOfReach();
        }
        return result.outcome;
    }

    private notifyWorkbenchOutOfReach(): void {
        this.notificationService.toastWarningNotification(
            'The workbench for this project is already open in another browser tab. Your request was sent to it.',
            'Workbench already open'
        );
    }

    private hasClosedCachedWindow(projectId: ProjectId): boolean {
        const cached = this.openedWindows.get(projectId);
        return !!cached && cached.closed;
    }

    private recordAuditOpen(request: WorkbenchOpenRequest): void {
        if (request.auditId) {
            this.userCustomizationService.updateMostRecentAudit(request.projectId, request.auditId);
        }
    }

    createWorkbenchUrl(request: WorkbenchOpenRequest): string {
        const queryParams: Params = {
            projectId: request.projectId,
            auditId: request.auditId,
            deviceId: request.deviceId,
            activeTestRun: request.activeTestRun,
            autoStartManualTest: request.autoStartManualTest !== undefined ? `${request.autoStartManualTest}` : undefined,
            suppressAutoResumeTestRun: request.suppressAutoResumeTestRun !== undefined ? `${request.suppressAutoResumeTestRun}` : undefined,
            browserExpeditionStartConfig: request.browserExpeditionStartConfig
                ? JSON.stringify(request.browserExpeditionStartConfig)
                : undefined,
            openTestObjectInExpeditionConfig: request.openTestObjectInExpeditionConfig
                ? JSON.stringify(request.openTestObjectInExpeditionConfig)
                : undefined,
            childWorkbench: request.childWorkbench ? `${request.childWorkbench}` : undefined,
            popoutConsoleId: request.popoutConsoleId
        };
        return this.router.serializeUrl(this.router.createUrlTree(['workbench'], {queryParams}));
    }
}
